import {
  SERVICE_STATUS,
  sortServices,
  statusOf,
} from "./ServiceDeployments.support";
import {
  CheckCircleRounded as LatestIcon,
  ErrorRounded as ErrorIcon,
  RefreshRounded as RefreshIcon,
  WarningRounded as OutdatedIcon,
} from "@mui/icons-material";
import {
  Chip,
  CircularProgress,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import { readableDate } from "common/dates";
import { toSentenceCase } from "common/strings";
import Component from "components/Component";
import Button from "components/common/Button";
import * as config from "config";
import { useCallback, useEffect, useMemo, useState } from "react";
import * as adminService from "services/adminService";
import * as utils from "utils";

const versionUrl = (basePath) => config.endpoints.root(`${basePath}/version`);

const LATEST_VERSION = process.env.REACT_APP_VERSION;

// The patch number encodes the release date as (month + 10) followed by a
// two-digit day, e.g. 1925 is September 25 (see scripts/version.py).
const releaseDateLabel = (version) => {
  const match = /^\d+\.\d+\.(\d+)/.exec(version);
  if (!match) return null;
  const patch = Number(match[1]);
  const month = Math.floor(patch / 100) - 10;
  const day = patch % 100;
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return new Date(2000, month - 1, day).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
  });
};

const stackStatusColour = (status) => {
  if (!status) return "default";
  if (status.endsWith("_IN_PROGRESS")) return "warning";
  if (status.includes("ROLLBACK") || status.includes("FAILED")) return "error";
  if (status.endsWith("_COMPLETE")) return "success";
  return "default";
};

const useServiceVersions = (services) => {
  const [versions, setVersions] = useState({});

  const fetchVersions = useCallback(async () => {
    const mounted = (services ?? []).filter((s) => s.base_path);
    setVersions(
      Object.fromEntries(mounted.map((s) => [s.name, { status: "loading" }]))
    );
    const setResult = (name, result) =>
      setVersions((prev) => ({ ...prev, [name]: result }));
    await Promise.all(
      mounted.map(async (service) => {
        try {
          const response = await utils.authenticatedFetchNoRetry(
            versionUrl(service.base_path)
          );
          const body = await response.json();
          setResult(service.name, { status: "ok", version: body.version });
        } catch (e) {
          setResult(service.name, {
            status: e?.status === 404 ? "missing" : "error",
            error: String(e?.message ?? e),
          });
        }
      })
    );
  }, [services]);

  useEffect(() => {
    fetchVersions();
  }, [fetchVersions]);

  return { versions, refetchVersions: fetchVersions };
};

const VersionCell = ({ service, result }) => {
  if (!service.base_path) {
    return service.version ?? "—";
  }
  if (!result || result.status === "loading") {
    return <Skeleton width={125} />;
  }
  if (result.status === "missing") {
    return (
      <Tooltip title={`GET ${versionUrl(service.base_path)} returned 404`}>
        <Typography variant={"body2"} color={"text.disabled"}>
          {"No version endpoint"}
        </Typography>
      </Tooltip>
    );
  }
  if (result.status === "error") {
    return "—"; // The status column flags it as unreachable
  }
  return (
    <Typography variant={"body2"}>{result.version ?? "Unknown"}</Typography>
  );
};

const StatusIcon = ({ icon: Icon, color, title }) => (
  <Tooltip title={title}>
    <Icon
      color={color}
      fontSize={"small"}
      sx={{ display: "block", mx: "auto" }}
    />
  </Tooltip>
);

const StatusCell = ({ service, result }) => {
  const status = statusOf(service, result, LATEST_VERSION);
  if (status === SERVICE_STATUS.LOADING) {
    return <Skeleton width={16} sx={{ mx: "auto" }} />;
  }
  if (status === SERVICE_STATUS.ERROR) {
    return (
      <StatusIcon
        icon={ErrorIcon}
        color={"error"}
        title={`Unreachable: ${result.error}`}
      />
    );
  }
  if (!status) {
    return "—";
  }
  if (status === SERVICE_STATUS.LATEST) {
    return (
      <StatusIcon
        icon={LatestIcon}
        color={"success"}
        title={"Matches latest release"}
      />
    );
  }
  const since = releaseDateLabel(LATEST_VERSION) ?? "the last release";
  return (
    <StatusIcon
      icon={OutdatedIcon}
      color={"warning"}
      title={`Latest release version is ${LATEST_VERSION}, this may just mean the service hasn't changed or been redeployed since ${since}`}
    />
  );
};

const StackStatusCell = ({ service }) => (
  <Chip
    color={stackStatusColour(service.stack_status)}
    label={
      service.stack_status
        ? toSentenceCase(service.stack_status)
        : "Not deployed"
    }
    variant={service.stack_status ? "filled" : "outlined"}
    size={"small"}
  />
);

const COLUMNS = [
  { key: "name", label: "Service" },
  { key: "version", label: "Version", sx: { width: 220 } },
  { key: "status", label: "Status", align: "center" },
  { key: "deployed_at", label: "Last deployed" },
  { key: "stack_status", label: "Stack Status" },
];

const SortableHeader = ({ sort, onSort }) => (
  <TableHead>
    <TableRow>
      {COLUMNS.map((column) => {
        const isActive = sort.key === column.key;
        return (
          <TableCell
            key={column.key}
            align={column.align}
            sx={column.sx}
            sortDirection={isActive ? sort.direction : false}
          >
            <TableSortLabel
              active={isActive}
              direction={isActive ? sort.direction : "asc"}
              onClick={() => onSort(column.key)}
            >
              {column.label}
            </TableSortLabel>
          </TableCell>
        );
      })}
    </TableRow>
  </TableHead>
);

const ServiceDeployments = () => {
  const {
    data,
    isFetching,
    refetch,
  } = adminService.useGetServiceDeploymentsQuery(undefined, {
    pollingInterval: 60000,
  });
  const services = data?.services;
  const { versions, refetchVersions } = useServiceVersions(services);
  const [sort, setSort] = useState({ key: "name", direction: "asc" });

  const sortedServices = useMemo(
    () => sortServices(services ?? [], versions, LATEST_VERSION, sort),
    [services, versions, sort]
  );

  const handleSort = (key) =>
    setSort((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));

  const handleRefresh = () => {
    refetch();
    refetchVersions();
  };

  return (
    <Component
      title={"Deployment Status"}
      subtitle={data?.stage ? `Stack state for stage ${data.stage}` : undefined}
      isFetching={isFetching}
      fadeOnFetch={false}
      options={
        <Button
          startIcon={<RefreshIcon />}
          size={"small"}
          onClick={handleRefresh}
          isDisabled={isFetching}
        >
          {"Refresh"}
        </Button>
      }
    >
      {!services && isFetching ? (
        <CircularProgress size={24} />
      ) : (
        <Table size={"small"}>
          <SortableHeader sort={sort} onSort={handleSort} />
          <TableBody>
            {sortedServices.map((service) => {
              const result = versions[service.name];
              return (
                <TableRow key={service.name} hover>
                  <TableCell>{toSentenceCase(service.name)}</TableCell>
                  <TableCell>
                    <VersionCell service={service} result={result} />
                  </TableCell>
                  <TableCell align={"center"}>
                    <StatusCell service={service} result={result} />
                  </TableCell>
                  <TableCell>
                    {service.deployed_at
                      ? readableDate(service.deployed_at, { includeTime: true })
                      : "—"}
                  </TableCell>
                  <TableCell>
                    <StackStatusCell service={service} />
                  </TableCell>
                </TableRow>
              );
            })}
            {services?.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography color={"text.secondary"}>
                    {"No deployable services found."}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}
    </Component>
  );
};

export default ServiceDeployments;
